import TextField from '@mui/material/TextField';
import { useEffect, useState } from 'react';

import { ExpertRequestCloseReasonCode } from '@/__generated__/graphql';
import Button from '@/componentsv2/Button';
import Dialog from '@/componentsv2/Dialog';
import Input from '@/componentsv2/Input';

import { CLOSE_REASON_LABELS, CLOSE_REASON_OPTIONS } from '../../utils/closeReasons';

// Mirror of the API's maxCloseReasonLen (expertrequest.go) so the client caps the
// free text before submit, matching the ER-title length contract (both 255).
const MAX_CLOSE_REASON_LEN = 255;

export interface CloseRequestModalProps {
  open: boolean;
  onCancel: () => void;
  onConfirm: (input: {
    closeReasonCode: ExpertRequestCloseReasonCode;
    closeReason?: string;
  }) => void;
  submitting?: boolean;
  error?: string | null;
  /** When > 0, the modal blocks closing and shows an informational notice instead. */
  openConsultationCount?: number;
}

/**
 * Close-request confirm modal (ENG-5290 T2). A reason code is mandatory; free
 * text is shown (and required) only for the "Other" code.
 */
export function CloseRequestModal({
  open,
  onCancel,
  onConfirm,
  submitting,
  error,
  openConsultationCount,
}: CloseRequestModalProps): JSX.Element {
  const [code, setCode] = useState<ExpertRequestCloseReasonCode | ''>('');
  const [freeText, setFreeText] = useState('');

  // The dialog stays mounted when closed, so reset the picker each time it opens.
  useEffect(() => {
    if (open) {
      setCode('');
      setFreeText('');
    }
  }, [open]);

  const isOther = code === ExpertRequestCloseReasonCode.Other;
  const canConfirm = code !== '' && (!isOther || freeText.trim().length > 0) && !submitting;
  const hasOpenConsultations = (openConsultationCount ?? 0) > 0;

  function handleConfirm() {
    if (code === '') return;
    onConfirm({ closeReasonCode: code, closeReason: isOther ? freeText.trim() : undefined });
  }

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" disablePortal={false}>
      <div data-testid="er-close-request-modal" className="flex flex-col gap-16 p-24">
        <h2 className="hd-5">Close request</h2>

        {hasOpenConsultations ? (
          <>
            <p className="text-on-surface-secondary body-1">
              This expert request has open consultations and can’t be closed. Try again after the
              consultations are finalized.
            </p>

            <div className="flex justify-end">
              {/* The tertiary Button hardcodes its brand color with no override prop, so
                  !text-on-surface-secondary is the only way to get the muted, dim-grey
                  dismiss affordance the design calls for (ENG-5318 review). */}
              <Button
                variant="tertiary"
                className="uppercase !text-on-surface-secondary"
                onClick={onCancel}
              >
                Dismiss
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-on-surface-secondary body-1">
              Sourcing will stop. You can reopen the request later if needed.
            </p>

            <TextField
              select
              fullWidth
              size="small"
              label="Reason (required)"
              value={code}
              onChange={(e) => setCode(e.target.value as ExpertRequestCloseReasonCode | '')}
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }}
            >
              <option value="" disabled>
                Select a reason…
              </option>
              {CLOSE_REASON_OPTIONS.map((c) => (
                <option key={c} value={c}>
                  {CLOSE_REASON_LABELS[c]}
                </option>
              ))}
            </TextField>

            {isOther ? (
              <Input
                label="Details"
                required
                multiline
                minRows={2}
                value={freeText}
                onChange={(e) => setFreeText(e.target.value)}
                placeholder="Tell us why you’re closing this request"
                inputProps={{ maxLength: MAX_CLOSE_REASON_LEN }}
              />
            ) : null}

            {error ? <p className="text-red-500 body-2">{error}</p> : null}

            <div className="flex justify-end gap-8">
              <Button variant="tertiary" onClick={onCancel}>
                Cancel
              </Button>
              <Button
                variant="primary"
                loading={submitting}
                disabled={!canConfirm}
                onClick={handleConfirm}
              >
                Close request
              </Button>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
}
